import React, { useContext, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import 'boxicons/css/boxicons.min.css';
import '../../styles/MenuPage.css';

// Content panels
import PatientListPage from '../PatientManagement/PatientListPage.jsx';
import OTBooking from '../OTManagement/OTBookingList/OTBookingList.jsx';
import UserListPage from '../UserManagement/UserListPage.jsx';
import RoleListPage from '../RoleManagement/RoleListPage.jsx';
import PermissionManagement from '../RoleManagement/PermissionManagement.jsx';
import IncompleteForms from '../components/IncompleteForms';
import Dashboard from '../components/Dashboard.js';
import DoctorDashboard from '../components/DoctorDashboard.js';
import NurseDashboard from '../components/NurseDashboard.js';
import OTRooms from '../OTManagement/OTRooms/OTRooms.jsx';
import FeedbackListPage from '../FeedbackManagement/FeedbackListPage.jsx';
import InventoryListPage from '../InventoryManagement/InventoryListPage.jsx';

import { API_BASE_URL } from '../../lib/constants';
import { AuthContext } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

// Hooks and Constants
import { useMenuState } from './hooks/useMenuState';
import { useNotifications } from './hooks/useNotifications';
import { navGroups, permissionMap } from './constants/menuConstants';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';

const MenuPage = () => {
  const { 
    userRole, 
    permissions, 
    setIsAuthenticated, 
    setUserRole, 
    setPermissions, 
    setFullname, 
    setLastlogin, 
    setHospitalId 
  } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const notificationRef = useRef(null);

  const {
    activeMenu,
    setActiveMenu,
    sidebarHidden,
    setSidebarHidden,
    sidebarCollapsed,
    isMobile,
    isTablet,
    expandedMenus,
    toggleSidebar,
    toggleSubMenu,
    handleMenuClick,
    getMainMargin
  } = useMenuState();

  const {
    notificationsVisible,
    setNotificationsVisible,
    delayAlerts,
    nurseNotifications,
    getNotificationCount,
    getNotificationTitle
  } = useNotifications(userRole);

  // ── Permission filtering ──────────────────────────────────────────────────
  const allowedNavGroups = useMemo(() => {
    const filterByPermission = (item) => {
      const normalizedRole = (userRole || '').toLowerCase();
      
      // Admin gets everything automatically
      if (normalizedRole === 'admin') return item;

      // Role-based hard exclusions (as requested by USER)
      if (normalizedRole.includes('doctor')) {
        const docExclusions = ['User Management', 'Patient Feedback', 'Invoice Management', 'Role List', 'Role Management', 'User List'];
        if (docExclusions.includes(item.name)) return null;
      }
      if (normalizedRole.includes('nurse')) {
        const nurseExclusions = ['User Management', 'Patient Feedback', 'Invoice Management', 'Role List', 'Role Management', 'User List'];
        if (nurseExclusions.includes(item.name)) return null;
      }

      if (item.subItems) {
        const filteredSubItems = item.subItems.map(filterByPermission).filter(Boolean);
        if (filteredSubItems.length > 0) return { ...item, subItems: filteredSubItems };
        
        // Check group permission if no subitems match but group has its own key
        const permission = permissionMap[item.name];
        if (permission && (permissions || []).includes(permission)) return item;
        return null;
      }

      const permission = permissionMap[item.name];
      if (!permission) return null;
      return (permissions || []).includes(permission) ? item : null;
    };

    return navGroups.map(group => ({
      ...group,
      items: group.items.map(filterByPermission).filter(Boolean)
    })).filter(group => group.items.length > 0);
  }, [userRole, permissions]);

  useEffect(() => {
    if (!permissions || permissions.length === 0) return;
    if (userRole === 'Admin') return;
    const hasDashboardView = permissions.includes('Dashboard View');
    if (activeMenu === 'Dashboard' && !hasDashboardView) {
      const allAllowedItems = allowedNavGroups.flatMap(g => g.items);
      const firstMenu = allAllowedItems[0];
      if (firstMenu) setActiveMenu(firstMenu.subItems ? firstMenu.subItems[0].name : firstMenu.name);
    }
  }, [permissions, userRole, allowedNavGroups, activeMenu, setActiveMenu]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
  };

  const handleLogout = async () => {
    const csrfToken = getCookie("csrf_access_token");
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        credentials: 'include',
      });
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      setIsAuthenticated(false);
      setUserRole(null);
      setPermissions([]);
      setFullname(null);
      setLastlogin(null);
      setHospitalId(null);
      navigate('/');
    }
  };

  const getDashboardComponent = () => {
    const role = (userRole || '').toLowerCase();
    if (role.includes('nurse')) return <NurseDashboard />;
    if (role.includes('doctor')) return <DoctorDashboard />;
    return <Dashboard />;
  };

  const renderNotifications = () => {
    const isNurse = userRole === 'Nurse';
    const isAdmin = userRole === 'Admin';

    const nurseItems = nurseNotifications.map((n, idx) => (
      <div key={`nurse-${idx}`} className="border-l-4 p-3 mb-2 rounded text-sm bg-blue-50 border-blue-500 animate-fade-in">
        <div className="font-bold text-blue-900 truncate">{n.patient_name}</div>
        <div className="text-blue-700 text-[10px] font-bold uppercase tracking-wider">PRE-OP ORDER | Room: {n.room}</div>
        <div className="mt-1 text-blue-800 font-medium line-clamp-1">{n.proposed_surgery}</div>
      </div>
    ));

    const delayItems = delayAlerts.map((a, idx) => (
      <div key={`delay-${idx}`} className="bg-red-50 border-l-4 border-red-500 p-3 mb-2 rounded text-sm animate-fade-in">
        <div className="font-bold text-red-900 truncate">{a.patient_name}</div>
        <div className="text-red-700 text-[10px] font-bold uppercase tracking-wider">SURGERY DELAY | {a.delay_minutes} MINS</div>
      </div>
    ));

    if (isAdmin) {
      if (nurseItems.length === 0 && delayItems.length === 0) {
        return <div className="text-center text-slate-400 py-6 font-bold text-xs uppercase tracking-widest">No Active Alerts</div>;
      }
      return (
        <div className="space-y-1">
          {delayItems}
          {nurseItems}
        </div>
      );
    }

    if (isNurse) {
      return nurseItems.length === 0 
        ? <div className="text-center text-blue-400 py-6 font-bold text-xs uppercase tracking-widest">No Pre-Op Orders</div> 
        : nurseItems;
    }

    return delayItems.length === 0 
      ? <div className="text-center text-red-400 py-6 font-bold text-xs uppercase tracking-widest">No Surgery Delays</div> 
      : delayItems;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'Dashboard':
        return (
          <>
            <IncompleteForms />
            {(userRole || '').toLowerCase() === 'admin'
              ? <Dashboard />
              : (permissions?.includes('Dashboard View') ? getDashboardComponent() : <div className="p-10 text-center font-bold text-slate-400">Access Denied</div>)
            }
          </>
        );
      case 'Patients':        return <PatientListPage />;
      case 'OT Rooms':        return <OTRooms />;
      case 'OT Booking':      return <OTBooking />;
      case 'Patient Feedback': return <FeedbackListPage />;
      case 'Inventory':       return <InventoryListPage />;
      case 'User List':     return <UserListPage />;
      case 'Role List':           return <RoleListPage />;
      case 'Role Management':     return <PermissionManagement />;
      default:                return <div className="p-10 text-center font-bold text-slate-400">Select a menu item.</div>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-x-hidden">
      {/* Mobile overlay */}
      {(isMobile || isTablet) && !sidebarHidden && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300" onClick={() => setSidebarHidden(true)} />
      )}

      <Sidebar
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        isTablet={isTablet}
        sidebarHidden={sidebarHidden}
        allowedNavGroups={allowedNavGroups}
        activeMenu={activeMenu}
        expandedMenus={expandedMenus}
        toggleSubMenu={toggleSubMenu}
        handleMenuClick={handleMenuClick}
        userRole={userRole}
        handleLogout={handleLogout}
      />

      {/* MAIN CONTENT */}
      <main
        className="transition-all duration-300 min-h-screen flex flex-col bg-slate-50/50"
        style={{ marginLeft: getMainMargin() }}
      >
        <div className="p-4 md:p-8 flex-1">
          <Header
            isMobile={isMobile}
            isTablet={isTablet}
            toggleSidebar={toggleSidebar}
            activeMenu={activeMenu}
            notificationRef={notificationRef}
            notificationsVisible={notificationsVisible}
            setNotificationsVisible={setNotificationsVisible}
            getNotificationCount={getNotificationCount}
            getNotificationTitle={getNotificationTitle}
            renderNotifications={renderNotifications}
          />

          {/* Dynamic content */}
          <div className="mt-4 flex-1">
            <div className="content-wrapper">
              {renderContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MenuPage;
